import { trelloService } from './trello'

export interface SprintDetails {
  label: string
  description: string
  duration: string
  goal: string
  startDate?: Date
  endDate?: Date
}

export interface SprintCard {
  id: string
  name: string
  desc: string
  idList: string
  due?: string
  labels?: Array<string | { id: string; name: string; color: string }>
  idMembers?: string[]
  url: string
  comments?: any[]
  attachments?: any[]
  members?: any[]
  checklists?: any[]
  activity?: any[]
  listName?: string
  createdAt?: string
  updatedAt?: string
}

export interface SprintMetrics {
  totalCards: number
  completedCards: number
  incompleteCards: number
  velocity: number
  cycleTime: number
  leadTime: number
  scopeCreep: number
  completionRate: number
  averageCardsPerDay: number
}

export interface SprintInsights {
  bottlenecks: string[]
  teamPerformance: any[]
  commonIssues: string[]
  improvementSuggestions: string[]
  sentimentAnalysis: {
    positive: number
    negative: number
    neutral: number
  }
}

export interface SprintRetrospective {
  whatWentWell: string[]
  whatWentWrong: string[]
  actionItems: string[]
  teamMorale: 'positive' | 'negative' | 'neutral'
  technicalDebt: 'low' | 'medium' | 'high'
}

export interface SprintReport {
  summary: {
    totalCards: number
    completedCards: number
    incompleteCards: number
    velocity: number
    burndownData: any[]
  }
  insights: SprintInsights
  retrospective: SprintRetrospective
  analysisLog: AnalysisLog
}

export interface AnalysisLog {
  timestamp: string
  sprintLabel: string
  boardId: string
  dataFetching: {
    cardsFound: number
    listsFound: number
    commentsFetched: number
    attachmentsFetched: number
    membersFetched: number
    activityRecords: number
    errors: string[]
  }
  metricsCalculation: {
    totalCards: number
    completedCards: number
    completionRate: number
    velocity: number
    cycleTime: number
    leadTime: number
    scopeCreep: number
    listDistribution: Record<string, number>
    labelDistribution: Record<string, number>
  }
  insightsGeneration: {
    bottlenecks: string[]
    teamPerformance: any[]
    commonIssues: string[]
    improvementSuggestions: string[]
    sentimentAnalysis: {
      positive: number
      negative: number
      neutral: number
    }
  }
  retrospectiveGeneration: {
    whatWentWell: string[]
    whatWentWrong: string[]
    actionItems: string[]
    teamMorale: 'positive' | 'negative' | 'neutral'
    technicalDebt: 'low' | 'medium' | 'high'
  }
  processingTime: {
    dataFetching: number
    metricsCalculation: number
    insightsGeneration: number
    retrospectiveGeneration: number
    total: number
  }
}

export class SprintAnalyzer {
  private cards: SprintCard[] = []
  private comments: any[] = []
  private members: any[] = []
  private attachments: any[] = []
  private activity: any[] = []
  private analysisLog: AnalysisLog = {
    timestamp: new Date().toISOString(),
    sprintLabel: '',
    boardId: '',
    dataFetching: {
      cardsFound: 0,
      listsFound: 0,
      commentsFetched: 0,
      attachmentsFetched: 0,
      membersFetched: 0,
      activityRecords: 0,
      errors: []
    },
    metricsCalculation: {
      totalCards: 0,
      completedCards: 0,
      completionRate: 0,
      velocity: 0,
      cycleTime: 0,
      leadTime: 0,
      scopeCreep: 0,
      listDistribution: {},
      labelDistribution: {}
    },
    insightsGeneration: {
      bottlenecks: [],
      teamPerformance: [],
      commonIssues: [],
      improvementSuggestions: [],
      sentimentAnalysis: { positive: 0, negative: 0, neutral: 0 }
    },
    retrospectiveGeneration: {
      whatWentWell: [],
      whatWentWrong: [],
      actionItems: [],
      teamMorale: 'neutral',
      technicalDebt: 'low'
    },
    processingTime: {
      dataFetching: 0,
      metricsCalculation: 0,
      insightsGeneration: 0,
      retrospectiveGeneration: 0,
      total: 0
    }
  }

  async analyzeSprint(boardId: string, sprintLabel: string, sprintDetails: SprintDetails): Promise<SprintReport> {
    const startTime = Date.now()
    
    // Initialize analysis log
    this.analysisLog = {
      timestamp: new Date().toISOString(),
      sprintLabel,
      boardId,
      dataFetching: {
        cardsFound: 0,
        listsFound: 0,
        commentsFetched: 0,
        attachmentsFetched: 0,
        membersFetched: 0,
        activityRecords: 0,
        errors: []
      },
      metricsCalculation: {
        totalCards: 0,
        completedCards: 0,
        completionRate: 0,
        velocity: 0,
        cycleTime: 0,
        leadTime: 0,
        scopeCreep: 0,
        listDistribution: {},
        labelDistribution: {}
      },
      insightsGeneration: {
        bottlenecks: [],
        teamPerformance: [],
        commonIssues: [],
        improvementSuggestions: [],
        sentimentAnalysis: { positive: 0, negative: 0, neutral: 0 }
      },
      retrospectiveGeneration: {
        whatWentWell: [],
        whatWentWrong: [],
        actionItems: [],
        teamMorale: 'neutral',
        technicalDebt: 'low'
      },
      processingTime: {
        dataFetching: 0,
        metricsCalculation: 0,
        insightsGeneration: 0,
        retrospectiveGeneration: 0,
        total: 0
      }
    }

    console.log('🚀 Starting Sprint Analysis...')
    console.log('📊 Analysis Parameters:', { boardId, sprintLabel, sprintDetails })

    try {
      // 1. Fetch all data in memory
      const dataFetchStart = Date.now()
      await this.fetchSprintData(boardId, sprintLabel)
      this.analysisLog.processingTime.dataFetching = Date.now() - dataFetchStart
      
      // 2. AI-powered analysis with sprint context
      const insightsStart = Date.now()
      const insights = await this.generateInsights(sprintDetails)
      this.analysisLog.processingTime.insightsGeneration = Date.now() - insightsStart
      
      // 3. Generate comprehensive report
      const metricsStart = Date.now()
      const metrics = this.calculateMetrics()
      this.analysisLog.processingTime.metricsCalculation = Date.now() - metricsStart
      
      const retrospectiveStart = Date.now()
      const retrospective = this.generateRetrospective(insights)
      this.analysisLog.processingTime.retrospectiveGeneration = Date.now() - retrospectiveStart
      
      // Update final processing time
      this.analysisLog.processingTime.total = Date.now() - startTime
      
      // Generate final report
      const report: SprintReport = {
        summary: {
          totalCards: metrics.totalCards,
          completedCards: metrics.completedCards,
          incompleteCards: metrics.incompleteCards,
          velocity: metrics.velocity,
          burndownData: this.generateBurndownData()
        },
        insights,
        retrospective,
        analysisLog: this.analysisLog
      }
      
      console.log('✅ Sprint Analysis Complete!')
      console.log('📈 Analysis Summary:', {
        totalCards: metrics.totalCards,
        completionRate: `${metrics.completionRate}%`,
        velocity: `${metrics.velocity} cards/day`,
        processingTime: `${this.analysisLog.processingTime.total}ms`
      })
      
      // Log detailed analysis for review
      this.logDetailedAnalysis(report)
      
      // Clear data for privacy
      this.clearData()
      
      return report
    } catch (error) {
      console.error('❌ Sprint Analysis Failed:', error)
      this.analysisLog.dataFetching.errors.push(error instanceof Error ? error.message : String(error))
      throw error
    }
  }

  private async fetchSprintData(boardId: string, sprintLabel: string): Promise<void> {
    const startTime = Date.now()
    console.log('📊 Fetching sprint data...')
    
    try {
      // Fetch cards with the specific sprint label
      this.cards = await trelloService.getCardsByLabel(boardId, sprintLabel)
      console.log(`📋 Found ${this.cards.length} cards with label "${sprintLabel}"`)
      this.analysisLog.dataFetching.cardsFound = this.cards.length

      // Fetch board lists to map list IDs to names
      const boardLists = await trelloService.getBoardLists(boardId)
      const listMap = new Map(boardLists.map(list => [list.id, list.name]))
      console.log(`📋 Found ${boardLists.length} lists:`, boardLists.map(l => l.name))
      this.analysisLog.dataFetching.listsFound = boardLists.length

      // Log list distribution for debugging
      const listDistribution = this.cards.reduce((acc, card) => {
        const listName = listMap.get(card.idList) || 'Unknown'
        acc[listName] = (acc[listName] || 0) + 1
        return acc
      }, {} as Record<string, number>)
      
      console.log('📊 List Distribution:', listDistribution)
      this.analysisLog.metricsCalculation.listDistribution = listDistribution

      // Fetch additional data for each card
      const dataPromises = this.cards.map(async (card) => {
        try {
          const [comments, attachments, members, checklists, activity] = await Promise.allSettled([
            trelloService.getCardComments(card.id),
            trelloService.getCardAttachments(card.id),
            trelloService.getCardMembers(card.id),
            trelloService.getCardChecklists(card.id),
            trelloService.getCardActivity(card.id)
          ])

          return {
            ...card,
            listName: listMap.get(card.idList) || 'Unknown',
            comments: comments.status === 'fulfilled' ? comments.value : [],
            attachments: attachments.status === 'fulfilled' ? attachments.value : [],
            members: members.status === 'fulfilled' ? members.value : [],
            checklists: checklists.status === 'fulfilled' ? checklists.value : [],
            activity: activity.status === 'fulfilled' ? activity.value : []
          }
        } catch (error) {
          console.warn(`Failed to fetch data for card ${card.id}:`, error)
          this.analysisLog.dataFetching.errors.push(`Card ${card.id}: ${error}`)
          return {
            ...card,
            listName: listMap.get(card.idList) || 'Unknown'
          }
        }
      })

      this.cards = await Promise.all(dataPromises)
      
      // Aggregate all data for analysis
      this.comments = this.cards.flatMap(card => card.comments || [])
      this.attachments = this.cards.flatMap(card => card.attachments || [])
      this.members = this.cards.flatMap(card => card.members || [])
      this.activity = this.cards.flatMap(card => card.activity || [])
      
      // Log data summary
      this.analysisLog.dataFetching.commentsFetched = this.comments.length
      this.analysisLog.dataFetching.attachmentsFetched = this.attachments.length
      this.analysisLog.dataFetching.membersFetched = this.members.length
      this.analysisLog.dataFetching.activityRecords = this.activity.length
      
      console.log('📊 Data Summary:', {
        cards: this.cards.length,
        comments: this.comments.length,
        attachments: this.attachments.length,
        members: this.members.length,
        activity: this.activity.length
      })
      
    } catch (error) {
      console.error('❌ Failed to fetch sprint data:', error)
      this.analysisLog.dataFetching.errors.push(`Data fetching failed: ${error}`)
      throw error
    }
  }

  private calculateMetrics(): SprintMetrics {
    console.log('📈 Calculating metrics...')
    
    const totalCards = this.cards.length
    const completedCards = this.cards.filter(card => 
      card.idList?.includes('Done') || 
      card.idList?.includes('Complete') ||
      card.labels?.some(label => {
        // Handle both string labels and label objects
        const labelName = typeof label === 'string' ? label : label.name || '';
        return labelName.toLowerCase().includes('done');
      })
    ).length
    const incompleteCards = totalCards - completedCards
    const completionRate = totalCards > 0 ? Math.round((completedCards / totalCards) * 100 * 100) / 100 : 0

    // Calculate velocity (cards per day)
    const sprintDuration = this.estimateSprintDuration()
    const velocity = sprintDuration > 0 ? Math.round((totalCards / sprintDuration) * 100) / 100 : 0

    // Calculate cycle time (average time from start to completion)
    const cycleTime = this.calculateCycleTime()

    // Calculate lead time (average time from creation to completion)
    const leadTime = this.calculateLeadTime()

    // Estimate scope creep (cards added after sprint start)
    const scopeCreep = this.estimateScopeCreep()

    // Calculate average cards per day
    const averageCardsPerDay = sprintDuration > 0 ? Math.round((totalCards / sprintDuration) * 100) / 100 : 0

    // Log metrics for debugging
    const metrics = {
      totalCards,
      completedCards,
      incompleteCards,
      velocity,
      cycleTime,
      leadTime,
      scopeCreep,
      completionRate,
      averageCardsPerDay
    }
    
    console.log('📊 Calculated Metrics:', metrics)
    
    // Update analysis log
    this.analysisLog.metricsCalculation = {
      totalCards,
      completedCards,
      completionRate,
      velocity,
      cycleTime,
      leadTime,
      scopeCreep,
      listDistribution: this.analysisLog.metricsCalculation.listDistribution,
      labelDistribution: this.analysisLog.metricsCalculation.labelDistribution
    }

    return metrics
  }

  private async generateInsights(sprintDetails: SprintDetails): Promise<SprintInsights> {
    console.log('🧠 Generating AI insights...')
    
    const bottlenecks = this.identifyBottlenecks()
    const teamPerformance = this.analyzeTeamPerformance()
    const commonIssues = this.identifyCommonIssues()
    const improvementSuggestions = this.generateImprovementSuggestions(sprintDetails)
    const sentimentAnalysis = this.analyzeSentiment()

    const insights = {
      bottlenecks,
      teamPerformance,
      commonIssues,
      improvementSuggestions,
      sentimentAnalysis
    }
    
    console.log('🧠 Generated Insights:', {
      bottlenecks: bottlenecks.length,
      teamPerformance: teamPerformance.length,
      commonIssues: commonIssues.length,
      improvementSuggestions: improvementSuggestions.length,
      sentimentAnalysis
    })
    
    // Update analysis log
    this.analysisLog.insightsGeneration = insights

    return insights
  }

  private generateRetrospective(insights: SprintInsights): SprintRetrospective {
    console.log('📋 Generating retrospective...')
    
    const whatWentWell = this.identifySuccesses()
    const whatWentWrong = this.identifyProblems()
    const actionItems = this.generateActionItems(insights)
    const teamMorale = this.assessTeamMorale(insights.sentimentAnalysis)
    const technicalDebt = this.assessTechnicalDebt()

    const retrospective = {
      whatWentWell,
      whatWentWrong,
      actionItems,
      teamMorale,
      technicalDebt
    }
    
    console.log('📋 Generated Retrospective:', {
      whatWentWell: whatWentWell.length,
      whatWentWrong: whatWentWrong.length,
      actionItems: actionItems.length,
      teamMorale,
      technicalDebt
    })
    
    // Update analysis log
    this.analysisLog.retrospectiveGeneration = retrospective

    return retrospective
  }

  private identifyBottlenecks(): string[] {
    const bottlenecks: string[] = []
    
    // Analyze list distribution
    const listDistribution = this.cards.reduce((acc, card) => {
      const listName = card.listName || 'Unknown'
      acc[listName] = (acc[listName] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    // Identify stuck cards
    const stuckCards = this.cards.filter(card => 
      card.activity?.some(act => 
        act.type === 'updateCard' && 
        new Date(act.date).getTime() < Date.now() - 7 * 24 * 60 * 60 * 1000 // 7 days ago
      )
    )

    if (stuckCards.length > 0) {
      bottlenecks.push(`${stuckCards.length} cards have been stuck for over a week`)
    }

    // Identify list bottlenecks
    Object.entries(listDistribution).forEach(([listName, count]) => {
      if (count > this.cards.length * 0.3) { // More than 30% of cards in one list
        bottlenecks.push(`High concentration of cards in "${listName}" (${count} cards)`)
      }
    })

    return bottlenecks
  }

  private analyzeTeamPerformance(): any[] {
    const memberStats = this.members.reduce((acc, member) => {
      const memberCards = this.cards.filter(card => 
        card.idMembers?.includes(member.id)
      )
      
      if (!acc[member.id]) {
        acc[member.id] = {
          id: member.id,
          name: member.fullName || member.username,
          totalCards: 0,
          completedCards: 0,
          avgCycleTime: 0
        }
      }
      
      acc[member.id].totalCards += memberCards.length
      acc[member.id].completedCards += memberCards.filter(card => 
        card.idList?.includes('Done') || card.idList?.includes('Complete')
      ).length
      
      return acc
    }, {} as Record<string, any>)

    return Object.values(memberStats)
  }

  private identifyCommonIssues(): string[] {
    const issues: string[] = []
    
    // Analyze comments for common issues
    const commentText = this.comments.map(c => c.data?.text || '').join(' ').toLowerCase()
    
    if (commentText.includes('blocked') || commentText.includes('blocker')) {
      issues.push('Multiple blockers mentioned in comments')
    }
    
    if (commentText.includes('estimate') && commentText.includes('wrong')) {
      issues.push('Incorrect time estimates')
    }
    
    if (commentText.includes('bug') || commentText.includes('issue')) {
      issues.push('Bugs and issues discovered during development')
    }

    // Analyze due dates
    const overdueCards = this.cards.filter(card => 
      card.due && new Date(card.due) < new Date()
    )
    
    if (overdueCards.length > 0) {
      issues.push(`${overdueCards.length} cards are overdue`)
    }

    return issues
  }

  private generateImprovementSuggestions(sprintDetails: SprintDetails): string[] {
    const suggestions: string[] = []
    
    // Based on completion rate
    const completionRate = this.cards.length > 0 ? 
      this.cards.filter(c => c.idList?.includes('Done')).length / this.cards.length : 0
    
    if (completionRate < 0.8) {
      suggestions.push('Improve sprint planning and estimation')
    }
    
    if (completionRate < 0.6) {
      suggestions.push('Consider reducing sprint scope')
    }

    // Based on bottlenecks
    if (this.cards.length > 10) {
      suggestions.push('Implement better workflow management')
    }

    // Based on team performance
    const memberCount = new Set(this.members.map(m => m.id)).size
    if (memberCount > 5) {
      suggestions.push('Consider team capacity planning')
    }

    return suggestions
  }

  private analyzeSentiment(): { positive: number; negative: number; neutral: number } {
    const commentText = this.comments.map(c => c.data?.text || '').join(' ')
    const positiveWords = ['great', 'good', 'awesome', 'excellent', 'perfect', 'solved', 'completed']
    const negativeWords = ['bad', 'terrible', 'blocked', 'stuck', 'broken', 'failed', 'issue']
    
    let positive = 0
    let negative = 0
    let neutral = 0
    
    const words = commentText.toLowerCase().split(/\s+/)
    words.forEach(word => {
      if (positiveWords.some(pw => word.includes(pw))) positive++
      else if (negativeWords.some(nw => word.includes(nw))) negative++
      else neutral++
    })
    
    return { positive, negative, neutral }
  }

  private identifySuccesses(): string[] {
    const successes: string[] = []
    
    const completionRate = this.cards.length > 0 ? 
      this.cards.filter(c => c.idList?.includes('Done')).length / this.cards.length : 0
    
    if (completionRate > 0.8) {
      successes.push('High sprint completion rate')
    }
    
    if (this.comments.length > 0) {
      successes.push('Good team communication and collaboration')
    }
    
    if (this.attachments.length > 0) {
      successes.push('Good documentation and file sharing')
    }

    return successes
  }

  private identifyProblems(): string[] {
    const problems: string[] = []
    
    const completionRate = this.cards.length > 0 ? 
      this.cards.filter(c => c.idList?.includes('Done')).length / this.cards.length : 0
    
    if (completionRate < 0.6) {
      problems.push('Low sprint completion rate')
    }
    
    const overdueCards = this.cards.filter(card => 
      card.due && new Date(card.due) < new Date()
    )
    
    if (overdueCards.length > 0) {
      problems.push(`${overdueCards.length} overdue cards`)
    }

    return problems
  }

  private generateActionItems(insights: SprintInsights): string[] {
    const actionItems: string[] = []
    
    if (insights.bottlenecks.length > 0) {
      actionItems.push('Address workflow bottlenecks identified in analysis')
    }
    
    if (insights.commonIssues.some(issue => issue.includes('estimate'))) {
      actionItems.push('Improve estimation process for future sprints')
    }
    
    if (insights.sentimentAnalysis.negative > insights.sentimentAnalysis.positive) {
      actionItems.push('Address team morale and communication issues')
    }

    return actionItems
  }

  private assessTeamMorale(sentiment: { positive: number; negative: number; neutral: number }): 'positive' | 'negative' | 'neutral' {
    const total = sentiment.positive + sentiment.negative + sentiment.neutral
    if (total === 0) return 'neutral'
    
    const positiveRatio = sentiment.positive / total
    const negativeRatio = sentiment.negative / total
    
    if (positiveRatio > 0.6) return 'positive'
    if (negativeRatio > 0.4) return 'negative'
    return 'neutral'
  }

  private assessTechnicalDebt(): 'low' | 'medium' | 'high' {
    const commentText = this.comments.map(c => c.data?.text || '').join(' ').toLowerCase()
    
    const technicalDebtIndicators = [
      'refactor', 'technical debt', 'legacy', 'cleanup', 'rewrite',
      'hack', 'quick fix', 'temporary', 'workaround'
    ]
    
    const debtCount = technicalDebtIndicators.filter(indicator => 
      commentText.includes(indicator)
    ).length
    
    if (debtCount > 5) return 'high'
    if (debtCount > 2) return 'medium'
    return 'low'
  }

  private estimateSprintDuration(): number {
    // Default to 2 weeks if we can't determine from data
    return 14
  }

  private calculateCycleTime(): number {
    // Calculate average time from start to completion
    const completedCards = this.cards.filter(card => 
      card.idList?.includes('Done') || card.idList?.includes('Complete')
    )
    
    if (completedCards.length === 0) return 0
    
    let totalDays = 0
    let count = 0
    
    completedCards.forEach(card => {
      if (card.activity) {
        const startActivity = card.activity.find(act => 
          act.type === 'updateCard' && act.data?.listBefore
        )
        const endActivity = card.activity.find(act => 
          act.type === 'updateCard' && act.data?.listAfter?.name?.includes('Done')
        )
        
        if (startActivity && endActivity) {
          const startDate = new Date(startActivity.date)
          const endDate = new Date(endActivity.date)
          const days = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
          totalDays += days
          count++
        }
      }
    })
    
    return count > 0 ? Math.round((totalDays / count) * 100) / 100 : 0
  }

  private calculateLeadTime(): number {
    // Calculate average time from creation to completion
    const completedCards = this.cards.filter(card => 
      card.idList?.includes('Done') || card.idList?.includes('Complete')
    )
    
    if (completedCards.length === 0) return 0
    
    let totalDays = 0
    let count = 0
    
    completedCards.forEach(card => {
      if (card.createdAt && card.activity) {
        const endActivity = card.activity.find(act => 
          act.type === 'updateCard' && act.data?.listAfter?.name?.includes('Done')
        )
        
        if (endActivity) {
          const startDate = new Date(card.createdAt)
          const endDate = new Date(endActivity.date)
          const days = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
          totalDays += days
          count++
        }
      }
    })
    
    return count > 0 ? Math.round((totalDays / count) * 100) / 100 : 0
  }

  private estimateScopeCreep(): number {
    // Estimate cards added after sprint start
    // This is a simplified estimation
    const cardsWithActivity = this.cards.filter(card => card.activity && card.activity.length > 0)
    const cardsAddedMidSprint = cardsWithActivity.filter(card => {
      const firstActivity = card.activity?.[0]
      if (!firstActivity) return false
      
      // If first activity is not creation, it might be scope creep
      return firstActivity.type !== 'createCard'
    })
    
    return cardsAddedMidSprint.length
  }

  private generateBurndownData(): any[] {
    // Generate mock burndown data for now
    // In Phase 3, this will be real data based on card movements
    const days = 14 // 2 weeks
    const totalCards = this.cards.length
    const completedCards = this.cards.filter(card => 
      card.idList?.includes('Done') || card.idList?.includes('Complete')
    ).length
    
    const burndownData = []
    for (let day = 0; day <= days; day++) {
      const ideal = totalCards - (totalCards / days) * day
      const actual = day === days ? completedCards : Math.random() * completedCards
      burndownData.push({ day, ideal, actual })
    }
    
    return burndownData
  }

  private clearData(): void {
    this.cards = []
    this.comments = []
    this.members = []
    this.attachments = []
    this.activity = []
    console.log('🧹 Cleared sprint analysis data from memory')
  }

  private logDetailedAnalysis(report: SprintReport): void {
    console.log('📋 DETAILED ANALYSIS LOG')
    console.log('='.repeat(50))
    console.log('📊 Analysis Parameters:', {
      sprintLabel: this.analysisLog.sprintLabel,
      boardId: this.analysisLog.boardId,
      timestamp: this.analysisLog.timestamp
    })
    
    console.log('📊 Data Fetching Summary:', this.analysisLog.dataFetching)
    console.log('📈 Metrics Calculation:', this.analysisLog.metricsCalculation)
    console.log('🧠 Insights Generation:', this.analysisLog.insightsGeneration)
    console.log('📋 Retrospective Generation:', this.analysisLog.retrospectiveGeneration)
    console.log('⏱️ Processing Times:', this.analysisLog.processingTime)
    
    console.log('📊 Final Report Summary:', {
      totalCards: report.summary.totalCards,
      completionRate: `${this.analysisLog.metricsCalculation.completionRate}%`,
      velocity: `${report.summary.velocity} cards/day`,
      bottlenecks: report.insights.bottlenecks.length,
      actionItems: report.retrospective.actionItems.length
    })
    console.log('='.repeat(50))
  }
} 